import { IdentityDocumentAnalysisSideStatus } from "@/services/UserIdentitiesService";
import type { DocumentAddressDto, DocumentAnalysisResultDto, IdentityDocumentAnalysisResponse, IdentityOrchestratorEnvelope } from "@/services/UserIdentitiesService";
import type { DocumentProcessingResult } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { resolveErrorMessage } from "@/screen/Identities/IdentityForm/identityErrorMessages";
import { normalizePersonName } from "@/utils/personNameRules";

// Partículas que forman parte del apellido siguiente ("de la Fuente", "del Río", "van der Berg").
const SurnameParticles = new Set(["de", "del", "la", "las", "los", "y", "i", "da", "das", "do", "dos", "van", "von", "der", "di", "du"]);

/**
 * Reparte los apellidos leídos del DNI entre primer y segundo apellido. Las partículas se unen al apellido
 * que las sigue, así que "DE LA FUENTE LOPEZ" queda como "DE LA FUENTE" + "LOPEZ".
 */
function splitDetectedSurnames(surnames: string): Pick<DocumentProcessingResult, "lastName" | "secondLastName">
{
    const groups: string[] = [];
    let pendingParticles: string[] = [];
    for(const word of surnames.split(" "))
    {
        if(SurnameParticles.has(word.toLowerCase()))
        {
            pendingParticles.push(word);
            continue;
        }
        groups.push([...pendingParticles, word].join(" "));
        pendingParticles = [];
    }
    if(pendingParticles.length > 0)
    {
        // Partículas al final (lectura incompleta): se pegan al último apellido para no perderlas.
        if(groups.length > 0)
        {
            groups[groups.length - 1] += ` ${pendingParticles.join(" ")}`;
        }
        else
        {
            groups.push(pendingParticles.join(" "));
        }
    }

    if(groups.length === 0)
    {
        return {};
    }
    if(groups.length === 1)
    {
        return {lastName: groups[0]};
    }
    return {lastName: groups[0], secondLastName: groups.slice(1).join(" ")};
}

/**
 * Nombre y apellidos a partir del análisis del DNI. detectedName es SOLO el nombre de pila (campo FirstName del
 * modelo), así que va entero al nombre: antes se troceaba por espacios y un "ROSA -MARIA" acababa con
 * "-MARIA" como primer apellido. Los apellidos salen de detectedLastName. Todo pasa por la misma limpieza
 * que el backend (PersonNameRules).
 */
export function buildNamesFromAnalysis(detectedName: string | null | undefined, detectedLastName: string | null | undefined): Pick<DocumentProcessingResult, "firstName" | "lastName" | "secondLastName">
{
    const firstName = normalizePersonName(detectedName);
    const surnames = normalizePersonName(detectedLastName);

    return{
        ...(firstName ? {firstName} : {}),
        ...(surnames ? splitDetectedSurnames(surnames) : {}),
    };
}


function mergeAnalysisFields(front?: DocumentAnalysisResultDto | null, back?: DocumentAnalysisResultDto | null): {detectedDni?: string | null; detectedName?: string | null; detectedLastName?: string | null; detectedBirthDate?: string | null; detectedExpirationDate?: string | null; detectedNationality?: string | null; detectedAddress?: DocumentAddressDto | null; extractedFields: Record<string, string>}
{
    const extracted: Record<string, string> =
    {
        ...(back?.extractedFields ?? {}),
        ...(front?.extractedFields ?? {}),
    };

    return{
        detectedDni: front?.detectedDni ?? back?.detectedDni,
        detectedName: front?.detectedName ?? back?.detectedName,
        detectedLastName: front?.detectedLastName ?? back?.detectedLastName,
        detectedBirthDate: front?.detectedBirthDate ?? back?.detectedBirthDate,
        detectedExpirationDate: front?.detectedExpirationDate ?? back?.detectedExpirationDate,
        detectedNationality: front?.detectedNationality ?? back?.detectedNationality,
        // En el DNI el domicilio está en el reverso
        detectedAddress: back?.detectedAddress ?? front?.detectedAddress,
        extractedFields: extracted,
    };
}

function inferDocumentType(docNumber: string | undefined): string
{
    if(!docNumber)
    {
        return "DNI";
    }
    return /^[XYZ]/i.test(docNumber.trim()) ? "NIE" : "DNI";
}


/**
 * Combina los mensajes de error del envelope filtrando por prefijo de cara.
 * El backend puede incluir mensajes como "[Front] ..." y "[Back] ..." en errors[].message.
 * Los mensajes de cada ítem se resuelven primero por código (tabla amigable) antes de
 * aplicar el prefijo de cara; si el código ya es suficientemente descriptivo se usa directamente.
 */
function buildSideErrorMessage(errors: {code: string; message: string}[]): string
{
    const resolveItem = (e: {code: string; message: string}): string => resolveErrorMessage(e.code?.trim() || "", e.message);

    const frontErrors = errors.filter((e) => e.message.startsWith("[Front]")).map((e) => resolveItem({...e, message: e.message.replace(/^\[Front\]\s*/, "").trim()}));
    const backErrors = errors.filter((e) => e.message.startsWith("[Back]")).map((e) => resolveItem({...e, message: e.message.replace(/^\[Back\]\s*/, "").trim()}));
    const otherErrors = errors.filter((e) => !e.message.startsWith("[Front]") && !e.message.startsWith("[Back]")).map(resolveItem);

    const parts: string[] = [];
    if(frontErrors.length > 0)
    {
        parts.push(`Anverso: ${frontErrors.join(" ")}`);
    }
    if(backErrors.length > 0)
    {
        parts.push(`Reverso: ${backErrors.join(" ")}`);
    }
    if(otherErrors.length > 0)
    {
        parts.push(otherErrors.join(" "));
    }
    return parts.join(" ") || resolveErrorMessage("INTERNAL_ERROR", "Error al analizar el documento.");
}

export type DocumentAnalysisMappedResult = | {ok: true; result: DocumentProcessingResult; warnings: string[]} | {ok: false; dniRequired: true; partialResult: DocumentProcessingResult; warnings: string[]};

export function mapAnalyzeDocumentResponseToProcessingResult(envelope: IdentityOrchestratorEnvelope<IdentityDocumentAnalysisResponse>, idFrontPreview: string, idBackPreview: string): DocumentAnalysisMappedResult
{
    const emptyPartial: DocumentProcessingResult =
    {
        success: false,
        documentType: "DNI",
        idFrontPreview,
        idBackPreview,
    };

    if(!envelope.success || !envelope.data)
    {
        const message = envelope.errors && envelope.errors.length > 0 ? buildSideErrorMessage(envelope.errors) : "Error al analizar el documento.";
        return {ok: false, dniRequired: true, partialResult: emptyPartial, warnings: [message]};
    }

    const data = envelope.data;
    const warnings: string[] = envelope.errors && envelope.errors.length > 0 ? [buildSideErrorMessage(envelope.errors)] : [];

    const merged = mergeAnalysisFields(data.front, data.back);
    const names = buildNamesFromAnalysis(merged.detectedName, merged.detectedLastName);
    const docNumber =
        merged.detectedDni?.trim()
        || merged.extractedFields["documentNumber"]
        || merged.extractedFields["numeroDocumento"];

    // Los documentos de validez permanente (DNI de mayores de 70 años) no traen fecha sino el
    // literal "PERMANENTE". Se detecta para premarcar el check; si la validez no se lee bien
    // (valor ausente o ilegible) se deja vacía para que el usuario la rellene a mano.
    const rawDateOfExpiration =
        merged.detectedExpirationDate
        || merged.extractedFields["dateOfExpiration"]
        || merged.extractedFields["fechaCaducidad"]
        || merged.extractedFields["fechaCaducidad_documento"];

    const hasPermanentIdExpiration = /PERMANENTE/i.test(rawDateOfExpiration ?? "");
    const dateOfExpiration = hasPermanentIdExpiration ? undefined : rawDateOfExpiration;

    const dateOfBirth =
        merged.detectedBirthDate
        || merged.extractedFields["dateOfBirth"]
        || merged.extractedFields["fechaNacimiento"];

    const partialResult: DocumentProcessingResult =
    {
        success: false,
        ...names,
        documentNumber: docNumber,
        dateOfBirth,
        dateOfExpiration,
        hasPermanentIdExpiration,
        nationality: merged.detectedNationality ?? undefined,
        address: merged.detectedAddress ?? undefined,
        documentType: inferDocumentType(docNumber),
        idFrontPreview,
        idBackPreview,
    };

    const bothSidesOk = data.frontStatus === IdentityDocumentAnalysisSideStatus.Ok && data.backStatus === IdentityDocumentAnalysisSideStatus.Ok;

    if(!bothSidesOk || !docNumber)
    {
        return {ok: false, dniRequired: true, partialResult, warnings};
    }

    return{
        ok: true,
        result: {...partialResult, success: true},
        warnings,
    };
}